/**
 * Budget Helpers - FinList Budget System
 * 
 * Pure utility functions extracted from budgetService.js
 * These functions have no side effects and don't access Firestore.
 */

import { 
  normalizeToCategoryTaxonomyId, 
  normalizeCategoryWithFallback,
  getCategoryDisplayName 
} from '@/constants/categoryCore';

/**
 * Get category label from category ID
 * Uses categoryCore.js as single source of truth for consistent naming
 * @param {string} categoryId - Category ID (any format)
 * @returns {string} Human-readable label from canonical source
 */
export function getCategoryLabel(categoryId) {
  if (!categoryId) return 'Unknown';
  
  // SSOT: First normalize to canonical ID, then get display name
  const canonicalId = normalizeToCategoryTaxonomyId(categoryId);
  
  // Try categoryCore first with canonical ID (single source of truth)
  if (canonicalId) {
    const displayName = getCategoryDisplayName(canonicalId);
    if (displayName && displayName !== 'Unknown') return displayName;
  }
  
  // Try with original ID (might be a custom category)
  const displayName = getCategoryDisplayName(categoryId);
  if (displayName && displayName !== 'Unknown') return displayName;
  
  // Fallback: prettify the ID
  return categoryId
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * @deprecated Use normalizeCategoryWithFallback from categoryCore.js instead
 * This export is kept for backward compatibility during migration
 * @param {string} categoryKey - Category key to normalize
 * @returns {string} Normalized category ID
 */
export function normalizeCategoryKey(categoryKey) {
  // LEGACY: Delegate to categoryCore.js single source of truth
  return normalizeCategoryWithFallback(categoryKey, 'miscellaneous');
}

/**
 * Format a budget amount for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'USD')
 * @returns {string} Formatted amount string
 */
export function formatBudgetAmount(amount, currency = 'USD') {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '$0.00';
  }
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (e) {
    return `$${amount.toFixed(2)}`;
  }
}

/**
 * Calculate percentage safely (avoiding division by zero)
 * @param {number} part - The part value
 * @param {number} total - The total value
 * @returns {number} Percentage (0-100)
 */
export function calculatePercentage(part, total) {
  if (!total || total === 0) return 0;
  return Math.min(100, Math.max(0, (part / total) * 100));
}

/**
 * Calculate burn rate percentage
 * @param {number} spent - Amount spent
 * @param {number} planned - Planned amount
 * @returns {number} Burn rate percentage
 */
export function calculateBurnRate(spent, planned) {
  return calculatePercentage(spent, planned);
}

/**
 * Determine alert level based on burn rate
 * @param {number} burnRate - Current burn rate percentage
 * @param {number} dayOfMonth - Current day of the month
 * @param {number} daysInMonth - Total days in the month
 * @returns {'ok'|'warning'|'danger'} Alert level
 */
export function getBurnRateAlert(burnRate, dayOfMonth, daysInMonth) {
  const expectedBurnRate = (dayOfMonth / daysInMonth) * 100;
  
  if (burnRate > 100) return 'danger';
  if (burnRate > expectedBurnRate + 20) return 'warning';
  return 'ok';
}
